import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';

/* ── Contratos (backend: manufacturing-extended.dto.ts) ─────────────────────
 *
 * GET  /manufacturing/shop-floor?all=&cellName=&machineName=&productionOrderId=&page=&limit=
 *      -> PaginatedResponseDto<ShopFloorCycleResponseDto>  (default: apenas ativos)
 * POST /manufacturing/shop-floor                          body: StartCycleRequestDto
 * POST /manufacturing/shop-floor/:id/complete             body: CompleteCycleRequestDto
 *
 * ShopFloorCycleResponseDto:
 *   id, productionOrderId?, cellName, machineName?, status (RUNNING|PAUSED|COMPLETED|CANCELLED),
 *   startedAt, endedAt?, durationMinutes, producedQuantity,
 *   qualityStatus (PENDING|APPROVED|REJECTED|REWORK), notes?, createdAt
 */

export interface ShopFloorCycle {
  id: string;
  productionOrderId?: string | null;
  cellName: string;
  machineName?: string | null;
  status: string;
  startedAt: string;
  endedAt?: string | null;
  /** Duracao em minutos (ciclos abertos: ate agora). */
  durationMinutes: number;
  producedQuantity: number;
  qualityStatus: string;
  notes?: string | null;
  createdAt: string;
}

export interface StartCycleInput {
  productionOrderId?: string;
  cellName: string;
  machineName?: string;
  notes?: string;
}

export interface CompleteCycleInput {
  producedQuantity: number;
  qualityStatus: 'PENDING' | 'APPROVED' | 'REJECTED' | 'REWORK';
  notes?: string;
}

interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

export const SHOP_FLOOR_KEY = ['manufacturing', 'shop-floor'] as const;

export function useShopFloorCycles(params?: {
  all?: boolean;
  cellName?: string;
  machineName?: string;
  productionOrderId?: string;
  page?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: [...SHOP_FLOOR_KEY, params],
    queryFn: () =>
      apiClient.get<PaginatedResponse<ShopFloorCycle>>('/manufacturing/shop-floor', {
        params: {
          ...(params?.all ? { all: 'true' } : {}),
          ...(params?.cellName ? { cellName: params.cellName } : {}),
          ...(params?.machineName ? { machineName: params.machineName } : {}),
          ...(params?.productionOrderId ? { productionOrderId: params.productionOrderId } : {}),
          page: String(params?.page ?? 1),
          limit: String(params?.limit ?? 20),
        },
      }),
  });
}

export function useStartCycle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: StartCycleInput) =>
      apiClient.post<ShopFloorCycle>('/manufacturing/shop-floor', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SHOP_FLOOR_KEY });
    },
  });
}

export function useCompleteCycle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & CompleteCycleInput) =>
      apiClient.post<ShopFloorCycle>(`/manufacturing/shop-floor/${id}/complete`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SHOP_FLOOR_KEY });
    },
  });
}